<template>
  <v-card v-if="trip" flat class="bg-background">
    <v-row
      no-gutters
      :align="smAndDown ? 'start' : 'center'"
      :class="smAndDown ? 'flex-column' : 'flex-row'"
    >
      <v-col md="8">
        <v-card-title v-if="trip.name">
          <span class="text-truncate"> {{ trip.name }} </span>
        </v-card-title>
        <v-card-title v-else>
          <span class="text-truncate">
            Trip to
            {{
              trip.destination?.name ?? trip.destination?.address ?? "unknown"
            }}
          </span>
        </v-card-title>
      </v-col>
      <v-col md="4" class="pr-3">
        <CustomTextButton @click="showTrip">
          Open trip plan on map
        </CustomTextButton>
      </v-col>
    </v-row>
    <v-card-subtitle v-if="!trip.name">
      <span class="text-truncate">
        from
        {{ trip.origin?.name ?? trip.origin?.address ?? "unknown" }}
      </span>
    </v-card-subtitle>

    <v-card-text>
      <!-- vehicle section -->
      <p v-if="trip.vehicle && trip.vehicle.licensePlate" class="pb-0">
        Using vehicle {{ trip.vehicle.licensePlate }}
      </p>
      <v-card class="py-4 pl-md-12 rounded-lg bg-background mb-10">
        <v-row class="flex-column flex-md-row">
          <v-col md="3">
            <!-- image section -->
            <v-skeleton-loader
              type="image"
              width="100%"
              height="140px"
              v-if="!trip.vehicle"
            />
            <v-img
              v-else
              contain
              :src="imageSrc(trip.vehicle)"
              aspect-ratio="1"
              :width="mdAndUp ? '100%' : '40%'"
              :height="mdAndUp ? '100%' : 'auto'"
              style="margin: auto"
              class="flex-grow-0"
            ></v-img>
          </v-col>
          <v-col md="9">
            <!-- details section -->
            <div v-if="trip.vehicle">
              <v-card-title>
                {{ trip.vehicle.name ?? "unnamed vehicle" }}
              </v-card-title>
              <v-card-text class="pr-12">
                <p class="mb-0">
                  {{ trip.vehicle.licensePlate ?? "unrecorded license plate" }}
                </p>
              </v-card-text>
            </div>
            <div v-else>
              <v-row no-gutters class="mb-4">
                <v-skeleton-loader type="heading" width="70%" />
                <v-skeleton-loader type="heading" width="30%" />
              </v-row>
              <v-skeleton-loader type="text" width="40%" />
              <v-skeleton-loader type="text" width="70%" class="mb-4" />
              <v-skeleton-loader type="heading" width="100%" />
            </div>
          </v-col>
        </v-row>
      </v-card>
      <!-- stats section -->
      <v-card class="mb-5 rounded-lg">
        <v-card-text>
          <v-row no-gutters class="mb-2">
            <v-col md="4"> Total travel time </v-col>
            <v-col md="4" class="pr-3">
              <div style="border-bottom: 1px solid lightgray; width: 100%">
                {{ niceDuration(trip.itinerary.totalTime) }}
              </div>
            </v-col>
            <v-col md="4">
              <div style="border-bottom: 1px solid lightgray; width: 100%">
                {{ Math.round(trip.itinerary.totalDrivingDistance / 1000) }} km
              </div>
            </v-col>
          </v-row>
          <v-row no-gutters class="mb-2">
            <v-col md="4"> Fuel used </v-col>
            <v-col md="8">
              <div style="border-bottom: 1px solid lightgray; width: 100%">
                {{
                  costEstimates.fuelUsedLitres !== undefined
                    ? `${costEstimates.fuelUsedLitres.toFixed(1)}L`
                    : "unknown"
                }}
              </div>
            </v-col>
          </v-row>
          <v-row no-gutters class="mb-2">
            <v-col md="4"> Fuel cost </v-col>
            <v-col md="8">
              <div style="border-bottom: 1px solid lightgray; width: 100%">
                {{
                  costEstimates.fuelCost !== undefined
                    ? `$${costEstimates.fuelCost.toFixed(2)}`
                    : "unknown"
                }}
              </div>
            </v-col>
          </v-row>
          <v-row no-gutters class="mb-2">
            <v-col md="4"> RUC cost </v-col>
            <v-col md="8">
              <div style="border-bottom: 1px solid lightgray; width: 100%">
                {{ costEstimates.rucCostText }}
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-card-text>
  </v-card>
  <v-card v-else flat>
    <v-card-text>
      If you're reading this, the trip couldn't be found.
      <CustomTextButton @click="showSavedTrips">
        Back to saved trips
      </CustomTextButton>
    </v-card-text>
  </v-card>
</template>
<script lang="ts" setup>
import CustomTextButton from "@/components/ui-elements/buttons/custom-buttons/CustomTextButton.vue";
import { useFleetStore } from "@/domains/fleet/fleetStore.ts";
import { useTripCostEstimates } from "@/domains/trips";
import { useTripStore } from "@/domains/trips/tripStore.ts";
import type { Vehicle } from "@/domains/vehicles/types";
import { useDashboardNavStore } from "@/stores/dashboardNavStore.ts";
import { getNiceDuration } from "@/utils/timeUtils";
import { computed } from "vue";
import { useDisplay } from "vuetify";

const tripStore = useTripStore();
const dashboardNavStore = useDashboardNavStore();
const fleetStore = useFleetStore();

const trip = computed(() =>
  tripStore.getTrips.find(
    (t) => t.id && t.id === dashboardNavStore.displayedTripId,
  ),
);

const { smAndDown, mdAndUp } = useDisplay();

const costEstimates = useTripCostEstimates(trip);

/**
 * Returns the source URL of the image associated with the vehicle.
 *
 * @param {Vehicle} vehicle - The vehicle object.
 * @return {string} The source URL of the image.
 */
function imageSrc(vehicle: Vehicle): string {
  // get uploaded image to car in first instance.
  const images = vehicle.imageSrcPaths;
  if (images[0]) return images[0];
  // get company logo image in third instance.
  if (fleetStore.logoSrc) {
    return fleetStore.logoSrc;
  }
  // get default image in fourth instance.
  return "/images/car_images/placeholder_car.png";
}

function showTrip() {
  if (trip.value) tripStore.showTrip(trip.value.id);
}

function showSavedTrips() {
  dashboardNavStore.displayedTripId = null;
  dashboardNavStore.displayedContent = "saved-trips";
}

function niceDuration(seconds: number): string {
  return getNiceDuration(seconds);
}
</script>
